import api from "./api";
import { MARKETING_EXCLUDED_KEY, refreshAdvertisingDecision } from "./marketingPrivacy";

// Attribution lives only in this tab's JS context, without fingerprinting or third-party calls.
// Only the explicit browser exclusion preference persists across reloads and logout.
// A reload is another landing visit. SPA navigation and Google sign-in keep the current attribution.
let activeVisit = null;
const INTERNAL_BROWSER_KEY = MARKETING_EXCLUDED_KEY;

export function isMarketingBrowserExcluded() {
  try { return window.localStorage.getItem(INTERNAL_BROWSER_KEY) === "1"; }
  catch { return false; }
}

export function setMarketingBrowserExcluded(excluded) {
  // Persist before reporting success: a blocked storage must not look like a saved preference.
  window.localStorage.setItem(INTERNAL_BROWSER_KEY, excluded ? "1" : "0");
  activeVisit = null;
  refreshAdvertisingDecision();
}

export function initializeInternalMarketingBrowser() {
  // Called by the admin funnel, not the landing. Respect an explicit opt-in for ad testing.
  if (window.localStorage.getItem(INTERNAL_BROWSER_KEY) === null) {
    setMarketingBrowserExcluded(true);
  }
  return isMarketingBrowserExcluded();
}
const fields = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id",
  "campaign_id", "adset_id", "ad_id", "campaign_name", "adset_name", "ad_name", "placement"];

export function trackLandingVisit(location = window.location, referrer = document.referrer) {
  if (isMarketingBrowserExcluded()) { clearAttribution(); return; }
  if (!["/", "/creators"].includes(location.pathname)) return;
  const params = new URLSearchParams(location.search);
  const tags = Object.fromEntries(fields.map((key) => [key, (params.get(key) || "").slice(0, 160)]));
  const tagged = fields.some((key) => tags[key]);
  const signature = JSON.stringify(tags);
  // Navigating internally does not overwrite the ad with a direct visit or create duplicate impressions.
  if (activeVisit && (!tagged || signature === activeVisit.signature)) return;
  if (!window.crypto?.randomUUID) return;
  const visitId = window.crypto.randomUUID();
  const promise = api.post("/public/marketing/visit", {
    visit_id: visitId, landing_path: location.pathname, referrer: referrer.slice(0, 2048), ...tags,
  }, { timeout: 4000 }).then(({ data }) => data.token || null).catch(() => null);
  activeVisit = { signature, promise };
}

export async function getAttributionToken() {
  if (isMarketingBrowserExcluded() || !activeVisit) return null;
  // Analytics must never hold up registration when its request is unavailable.
  const token = await Promise.race([activeVisit.promise, new Promise((resolve) => setTimeout(() => resolve(null), 1000))]);
  // The preference can change in another tab while the request is pending.
  return isMarketingBrowserExcluded() ? null : token;
}

export function clearAttribution() { activeVisit = null; }
